import { useId } from "react";
import {
  Button as AriaButton,
  Input as AriaInput,
  NumberField as AriaNumberField,
  NumberFieldProps as AriaNumberFieldProps,
  Group,
  ValidationResult,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { composeTailwindRenderProps } from "../../utils";
import type { Size } from "../../utils/controlStyles";
import { BaseInputProps, Description, FieldError, getInputBaseStyles, Label, useInputFocus } from "../Field";
import { Icon } from "../Icon";

const getNumberFieldStyles = (props: {
  isInvalid?: boolean;
  isDisabled?: boolean;
  isFocused?: boolean;
  transparent?: boolean;
  size?: Size;
  className?: string;
}): string => {
  // Get base input styles (includes state, background, sizing, padding)
  const baseStyles = getInputBaseStyles({
    isInvalid: props.isInvalid,
    isDisabled: props.isDisabled,
    isFocused: props.isFocused,
    transparent: props.transparent,
    size: props.size,
  });

  // Add NumberField-specific styles
  return twMerge(
    baseStyles,
    "pr-9", // Space for stepper buttons
    "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none", // Hide native spinners
    props.className
  );
};

/**
 * NumberField
 *
 * Numeric input with stepper controls and validation.
 */
export interface NumberFieldProps extends Omit<AriaNumberFieldProps, "size" | "className">, BaseInputProps {
  value?: number;
  defaultValue?: number;
  onChange?: (value: number) => void;
  minValue?: number;
  maxValue?: number;
  step?: number;
  formatOptions?: Intl.NumberFormatOptions;
  id?: string;
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  tooltip?: string;
  isRequired?: boolean;
  className?: string;
  validationResult?: ValidationResult;
  /**
   * Where to display the description text
   * - "below": Show below the field (default)
   * - "tooltip": Show as a tooltip on the label icon
   * - "inline": Show inline with the label
   * - "hidden": Don't display the description
   * @default "below"
   */
  descriptionPlacement?: "below" | "tooltip" | "inline" | "hidden";
}

export function NumberField({
  label,
  description,
  errorMessage,
  size = "md",
  tooltip,
  isRequired,
  transparent,
  validationResult,
  reserveErrorSpace = true,
  descriptionPlacement = "below",
  ...props
}: NumberFieldProps) {
  const id = useId();
  const { isFocused, handleFocus, handleBlur } = useInputFocus();

  return (
    <AriaNumberField
      {...props}
      id={id}
      isRequired={isRequired}
      className={composeTailwindRenderProps(props.className, "group flex flex-col gap-1")}
      step={props.step ?? 1}
      onChange={(value) => {
        if (typeof value === "number") {
          if (props.maxValue != null && value > props.maxValue) {
            props.onChange?.(props.maxValue);
          } else if (props.minValue != null && value < props.minValue) {
            props.onChange?.(props.minValue);
          } else {
            props.onChange?.(value);
          }
        }
      }}
      isInvalid={
        !!errorMessage ||
        (typeof props.value === "number" &&
          ((props.maxValue != null && props.value > props.maxValue) ||
            (props.minValue != null && props.value < props.minValue)))
      }
    >
      {({ isDisabled, isInvalid }) => {
        const isValueInvalid = isInvalid || !!errorMessage;
        return (
          <div className="flex flex-col gap-1">
            {label && (
              <Label
                size={size}
                tooltip={tooltip}
                description={
                  descriptionPlacement === "tooltip" || descriptionPlacement === "inline" ? description : undefined
                }
                descriptionPlacement={descriptionPlacement}
                isRequired={isRequired}
                htmlFor={id}
              >
                {label}
              </Label>
            )}
            <div className="relative">
              <Group className="relative">
                <AriaInput
                  onFocus={(e) => handleFocus(e, props.onFocus)}
                  onBlur={(e) => handleBlur(e, props.onBlur)}
                  className={getNumberFieldStyles({
                    isInvalid: isValueInvalid,
                    isDisabled,
                    isFocused,
                    transparent,
                    size,
                    className: typeof props.className === "string" ? props.className : undefined,
                  })}
                />
                <div className="absolute inset-y-[2px] right-[2px] flex flex-col divide-y divide-border-default border-l border-border-default overflow-hidden rounded-r-[calc(var(--control-border-radius)-2px)]">
                  <AriaButton
                    slot="increment"
                    isDisabled={isDisabled}
                    className="flex h-1/2 w-7 items-center justify-center bg-background-input hover:bg-background-input-hover active:bg-background-input-active disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Icon name="CaretUp" size={14} />
                  </AriaButton>
                  <AriaButton
                    slot="decrement"
                    isDisabled={isDisabled}
                    className="flex h-1/2 w-7 items-center justify-center bg-background-input hover:bg-background-input-hover active:bg-background-input-active disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Icon name="CaretDown" size={14} />
                  </AriaButton>
                </div>
              </Group>
            </div>
            {reserveErrorSpace ? (
              <div className="mt-1 min-h-[20px]">
                {errorMessage ? (
                  <FieldError size={size}>
                    {typeof errorMessage === "function" ? errorMessage(validationResult!) : errorMessage}
                  </FieldError>
                ) : description && descriptionPlacement === "below" ? (
                  <Description size={size}>{description}</Description>
                ) : null}
              </div>
            ) : (
              <>
                {errorMessage ? (
                  <FieldError size={size} className="mt-1">
                    {typeof errorMessage === "function" ? errorMessage(validationResult!) : errorMessage}
                  </FieldError>
                ) : description && descriptionPlacement === "below" ? (
                  <Description size={size} className="mt-1">
                    {description}
                  </Description>
                ) : null}
              </>
            )}
          </div>
        );
      }}
    </AriaNumberField>
  );
}
